import { DashboardHero } from '@/components/dashboard/DashboardHero';
import { OverviewCards } from '@/components/dashboard/overview-cards';
import { RecentTransactions } from '@/components/dashboard/recent-transactions';
import { IncomeExpenseChart } from '@/components/analytics/income-expense-chart';
import { CategoryPieChart } from '@/components/dashboard/category-pie-chart';
import { useActiveWorkspace } from '@/hooks/use-active-workspace';
import { BusinessDashboard } from '@/components/dashboard/business-dashboard';
import { GettingStarted } from '@/components/onboarding/getting-started';
import { useOnboarding } from '@/hooks/use-onboarding';
import { useAppStore } from '@/lib/data';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  ArrowRightLeft, Target, PiggyBank, Receipt,
  Bell, ChevronRight, Landmark, AlertCircle
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import { AddTransactionSheet } from '@/components/transactions/add-transaction-sheet';

// Quick action button
function QuickAction({ icon: Icon, label, to, color }: { icon: any; label: string; to: string; color: string }) {
  return (
    <Link to={to}>
      <div className="flex flex-col items-center gap-2 group cursor-pointer">
        <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <span className="text-xs font-semibold text-slate-600 text-center leading-tight">{label}</span>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { activeWorkspace } = useActiveWorkspace();
  const { user } = useAuth();
  const { transactions, bills, savingsGoals, loans } = useAppStore();
  const { isOnboardingCompleted, completeOnboarding, skipOnboarding } = useOnboarding();

  const personalTransactions = useMemo(
    () => transactions.filter(t => t.workspace === 'personal'),
    [transactions]
  );

  const businessTransactions = useMemo(
    () => transactions.filter(t => t.workspace === 'business'),
    [transactions]
  );

  // Upcoming bills (unpaid, due within 7 days)
  const upcomingBills = useMemo(() => {
    const now = new Date();
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return bills
      .filter(b => b.status === 'unpaid' && new Date(b.dueDate) <= in7Days)
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
      .slice(0, 3);
  }, [bills]);

  // Top savings goals
  const topGoals = useMemo(() =>
    savingsGoals.slice(0, 3),
    [savingsGoals]
  );

  const hasAnyData = transactions.length > 0 || bills.length > 0 || savingsGoals.length > 0 || loans.length > 0;
  const shouldShowDashboard = hasAnyData || isOnboardingCompleted;

  if (activeWorkspace === 'business') {
    return <BusinessDashboard transactions={businessTransactions} />;
  }

  if (!user) return null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-8 max-w-7xl mx-auto">

      {/* Hero */}
      <DashboardHero
        userName={user.user_metadata?.name || user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
        userId={user.id}
        avatarUrl={user.user_metadata?.avatar_url || null}
        transactions={personalTransactions}
      />

      {shouldShowDashboard ? (
        <>
          {/* Quick Actions */}
          <Card className="border-0 shadow-sm bg-white rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-around gap-2">
                <div className="flex flex-col items-center gap-2">
                  <AddTransactionSheet />
                </div>
                <QuickAction icon={ArrowRightLeft} label="Transactions" to="/dashboard/transactions" color="bg-gradient-to-br from-sky-500 to-blue-600" />
                <QuickAction icon={Target} label="Budgets" to="/dashboard/budgets" color="bg-gradient-to-br from-amber-500 to-orange-600" />
                <QuickAction icon={PiggyBank} label="Savings" to="/dashboard/savings" color="bg-gradient-to-br from-emerald-500 to-teal-600" />
                <QuickAction icon={Landmark} label="Loans" to="/dashboard/loans" color="bg-gradient-to-br from-rose-500 to-pink-600" />
                <QuickAction icon={Receipt} label="Bills" to="/dashboard/bills" color="bg-gradient-to-br from-violet-500 to-purple-600" />
              </div>
            </CardContent>
          </Card>

          {/* Overview Cards */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest">This Month</h2>
            </div>
            <OverviewCards transactions={personalTransactions} />
          </section>

          {/* Middle Row: Recent Transactions + Sidebar widgets */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Recent Transactions — 2 cols */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest">Recent Activity</h2>
                <Link to="/dashboard/transactions">
                  <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl text-xs font-bold">
                    View all <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
              <Card className="border-0 shadow-sm bg-white rounded-2xl">
                <CardContent className="p-5">
                  <RecentTransactions transactions={personalTransactions} />
                </CardContent>
              </Card>
            </div>

            {/* Right sidebar widgets — 1 col */}
            <div className="space-y-6">

              {/* Upcoming Bills */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest">Upcoming Bills</h2>
                  <Link to="/dashboard/bills">
                    <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl text-xs font-bold">
                      All <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                </div>
                <Card className="border-0 shadow-sm bg-white rounded-2xl">
                  <CardContent className="p-4 space-y-3">
                    {upcomingBills.length > 0 ? upcomingBills.map(bill => {
                      const dueDate = new Date(bill.dueDate);
                      const daysLeft = Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                      const isOverdue = daysLeft < 0;
                      const isUrgent = daysLeft <= 2;
                      return (
                        <div key={bill.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${isOverdue || isUrgent ? 'bg-rose-100' : 'bg-amber-100'}`}>
                              {isOverdue || isUrgent
                                ? <AlertCircle className="w-4 h-4 text-rose-600" />
                                : <Bell className="w-4 h-4 text-amber-600" />
                              }
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800 truncate">{bill.name}</p>
                              <p className={`text-xs font-medium ${isOverdue ? 'text-rose-600' : isUrgent ? 'text-amber-600' : 'text-slate-500'}`}>
                                {isOverdue ? 'Overdue' : daysLeft === 0 ? 'Due today' : `${daysLeft}d left`}
                              </p>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-slate-900 flex-shrink-0">{formatCurrency(bill.amount)}</span>
                        </div>
                      );
                    }) : (
                      <div className="text-center py-6">
                        <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No upcoming bills</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Savings Goals */}
              {topGoals.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest">Savings Goals</h2>
                    <Link to="/dashboard/savings">
                      <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl text-xs font-bold">
                        All <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                  <Card className="border-0 shadow-sm bg-white rounded-2xl">
                    <CardContent className="p-4 space-y-4">
                      {topGoals.map(goal => {
                        const pct = goal.targetAmount > 0 ? Math.min(100, (goal.currentAmount / goal.targetAmount) * 100) : 0;
                        return (
                          <div key={goal.id} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-slate-800 truncate max-w-[60%]">{goal.name}</span>
                              <span className="text-xs font-bold text-slate-500">{Math.round(pct)}%</span>
                            </div>
                            <Progress value={pct} className="h-2" />
                            <div className="flex justify-between text-xs text-slate-500">
                              <span>{formatCurrency(goal.currentAmount)}</span>
                              <span>{formatCurrency(goal.targetAmount)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </section>

          {/* Charts Row */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">Income vs Expenses</h2>
              <Card className="border-0 shadow-sm bg-white rounded-2xl">
                <CardContent className="p-5">
                  <IncomeExpenseChart transactions={personalTransactions} />
                </CardContent>
              </Card>
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">Spending by Category</h2>
              <Card className="border-0 shadow-sm bg-white rounded-2xl">
                <CardContent className="p-5">
                  <CategoryPieChart transactions={personalTransactions} />
                </CardContent>
              </Card>
            </div>
          </section>
        </>
      ) : (
        <GettingStarted onSkip={skipOnboarding} onComplete={completeOnboarding} />
      )}
    </div>
  );
}
